import React from 'react';
import { Gauge } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import type { Process } from '../type/Process';
import { CollapsibleFormSection } from './CollapsibleFormSection';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { MandatoryFieldLabel } from './MandatoryFieldLabel';

type Props = {
  formProcess: Process;
  setFormProcess: React.Dispatch<React.SetStateAction<Process>>;
  mandatoryFields: string[];
  onToggleMandatory: (key: string) => void;
};

type CuttingField = 'ae' | 'ap' | 'vc' | 'fz';

export const ProcessFormCuttingSection: React.FC<Props> = ({
  formProcess,
  setFormProcess,
  mandatoryFields,
  onToggleMandatory,
}) => {
  const { t } = useTranslation();

  const fields: CuttingField[] = ['ae', 'ap', 'vc', 'fz'];
  const setCount = fields.filter((f) => formProcess[f] !== null).length;
  const defaultOpen = setCount > 0;

  const countLabel = t('traceability.processManager.form.sections.countLabel', {
    set: setCount,
    total: fields.length,
  });

  const renderLabelRow = (fieldKey: string, labelText: string, inputId: string) => (
    <MandatoryFieldLabel
      fieldKey={fieldKey}
      label={labelText}
      htmlFor={inputId}
      mandatoryFields={mandatoryFields}
      onToggleMandatory={onToggleMandatory}
    />
  );

  return (
    <CollapsibleFormSection
      icon={<Gauge className="h-3 w-3" aria-hidden />}
      title={t('traceability.processManager.form.sections.cuttingParameters.title')}
      toggleLabel={t('traceability.processManager.form.sections.cuttingParameters.toggleLabel')}
      countLabel={countLabel}
      defaultOpen={defaultOpen}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          {renderLabelRow(
            'ae',
            `${t('traceability.processManager.form.prefillFields.ae.label')} (${t('traceability.processManager.form.prefillFields.ae.unit')})`,
            'process-ae'
          )}
          <SimpleInput
            name="process-ae"
            type="number"
            min={0}
            value={formProcess.ae ?? undefined}
            onChange={(val) =>
              setFormProcess((p) => ({ ...p, ae: val === null ? null : (val as number) }))
            }
            placeholder={t('traceability.processManager.form.prefillFields.ae.placeholder')}
          />
        </div>
        <div>
          {renderLabelRow(
            'ap',
            `${t('traceability.processManager.form.prefillFields.ap.label')} (${t('traceability.processManager.form.prefillFields.ap.unit')})`,
            'process-ap'
          )}
          <SimpleInput
            name="process-ap"
            type="number"
            min={0}
            value={formProcess.ap ?? undefined}
            onChange={(val) =>
              setFormProcess((p) => ({ ...p, ap: val === null ? null : (val as number) }))
            }
            placeholder={t('traceability.processManager.form.prefillFields.ap.placeholder')}
          />
        </div>
        <div>
          {renderLabelRow(
            'vc',
            `${t('traceability.processManager.form.prefillFields.vc.label')} (${t('traceability.processManager.form.prefillFields.vc.unit')})`,
            'process-vc'
          )}
          <SimpleInput
            name="process-vc"
            type="number"
            min={0}
            value={formProcess.vc ?? undefined}
            onChange={(val) =>
              setFormProcess((p) => ({ ...p, vc: val === null ? null : (val as number) }))
            }
            placeholder={t('traceability.processManager.form.prefillFields.vc.placeholder')}
          />
        </div>
        <div>
          {renderLabelRow(
            'fz',
            `${t('traceability.processManager.form.prefillFields.fz.label')} (${t('traceability.processManager.form.prefillFields.fz.unit')})`,
            'process-fz'
          )}
          <SimpleInput
            name="process-fz"
            type="number"
            min={0}
            value={formProcess.fz ?? undefined}
            onChange={(val) =>
              setFormProcess((p) => ({ ...p, fz: val === null ? null : (val as number) }))
            }
            placeholder={t('traceability.processManager.form.prefillFields.fz.placeholder')}
          />
        </div>
      </div>
    </CollapsibleFormSection>
  );
};
